import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  FlaskConical,
  Loader2,
  MousePointerClick,
  Rocket,
} from "lucide-react";

import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { StatusPill, SkeletonLine } from "@/screens/you/parts";
import { useExperiments, useCreateExperiment, useSetExperimentStatus } from "@/data/hooks";
import type {
  ComponentTree,
  CreateExperimentBody,
  Experiment,
  ExperimentStatus,
} from "@/data/services";
import { registry, type RegistryName } from "@dynamic-landing/components";
import { describeSchema, type FieldDescriptor, type SchemaLike } from "@/editor/schemaIntrospect";
import { getNodeProp } from "@/editor/treeOps";
import type { ComponentNode } from "@/editor/types";
import { AiRewrite } from "./Inspector";

/* ------------------------------------------------------------------ status */
const STATUS_TONE: Record<ExperimentStatus, "ok" | "pending" | "muted"> = {
  draft: "muted",
  running: "ok",
  paused: "pending",
  concluded: "muted",
};
const STATUS_LABEL: Record<ExperimentStatus, string> = {
  draft: "Draft",
  running: "Running",
  paused: "Paused",
  concluded: "Concluded",
};

/**
 * The Make builder's "Test" tab — A/B testing without leaving the canvas.
 *
 *  - Lists the active page's experiments compactly (name · status · traffic),
 *    each linking to Signals → A/B tests for the full Bayesian readout.
 *  - Creates a new test on the SELECTED block: pick a prop to vary, give the
 *    variant value, set traffic + goal, and launch. We build a two-variant
 *    experiment (control with no ops, variant with one set-prop op on the
 *    selected node), create it, then flip it to "running".
 */
export function TestTab({
  pageId,
  tree,
  selectedNode,
}: {
  pageId: string | undefined;
  tree: ComponentTree | null;
  selectedNode: ComponentNode | null;
}) {
  const nav = useNavigate();
  const expQ = useExperiments(pageId);
  const items = expQ.data?.items ?? [];

  return (
    <div className="space-y-5">
      {/* existing tests on this page */}
      <section className="space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="eyebrow px-0.5">On this page</p>
          <button
            onClick={() => nav("/signals/experiments")}
            className="inline-flex items-center gap-1 text-[12px] text-muted transition-colors hover:text-accent"
          >
            All A/B tests
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>

        {expQ.isLoading ? (
          <Card className="space-y-2 p-3">
            <SkeletonLine className="h-3 w-2/3" />
            <SkeletonLine className="h-3 w-1/3" />
          </Card>
        ) : items.length === 0 ? (
          <Card className="grid place-items-center px-4 py-6 text-center">
            <FlaskConical className="h-5 w-5 text-subtle" strokeWidth={1.5} />
            <p className="mt-2 text-[13px] font-medium text-ink-strong">No tests on this page yet</p>
            <p className="mt-1 max-w-[34ch] text-[12px] leading-relaxed text-muted">
              Select a block in the preview and create your first A/B test below.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((e) => (
              <ExperimentRow key={e.id} exp={e} onOpen={() => nav("/signals/experiments")} />
            ))}
          </div>
        )}
      </section>

      {/* create a new test */}
      <section className="space-y-2.5">
        <p className="eyebrow px-0.5">New A/B test</p>
        {/* Key by the selected block so the form (name / hypothesis / variant
            value) resets when the test target changes — otherwise a value typed
            for one block leaks onto another and you can launch a mislabeled test. */}
        <CreateTestForm
          key={selectedNode?.node_id ?? "none"}
          pageId={pageId}
          tree={tree}
          selectedNode={selectedNode}
        />
      </section>
    </div>
  );
}

/* ------------------------------------------------------------- experiment row */
function ExperimentRow({ exp, onOpen }: { exp: Experiment; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-card border border-hairline bg-paper px-3 py-2.5 text-left transition-colors hover:border-ink/20 dark:bg-white/[0.03]"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-ink-strong">{exp.name}</span>
        <span className="mt-0.5 block text-[11px] text-subtle">
          <span className="tabular-nums text-muted">{exp.allocation.traffic_pct}%</span> traffic
          {exp.variants.length > 0 && (
            <>
              {" · "}
              <span className="tabular-nums text-muted">{exp.variants.length}</span> variants
            </>
          )}
        </span>
      </span>
      <StatusPill tone={STATUS_TONE[exp.status]}>{STATUS_LABEL[exp.status]}</StatusPill>
      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-subtle" strokeWidth={1.75} />
    </button>
  );
}

/* ----------------------------------------------------------------- create form */
function CreateTestForm({
  pageId,
  tree,
  selectedNode,
}: {
  pageId: string | undefined;
  tree: ComponentTree | null;
  selectedNode: ComponentNode | null;
}) {
  const nav = useNavigate();
  const createExp = useCreateExperiment();
  const setStatus = useSetExperimentStatus();

  // Editable props of the selected node (so the marketer varies a real prop).
  const fields = useMemo<FieldDescriptor[]>(() => {
    if (!selectedNode) return [];
    const entry = (registry as Record<string, (typeof registry)[RegistryName] | undefined>)[
      selectedNode.component.name
    ];
    if (!entry) return [];
    return describeSchema(entry.schema as unknown as SchemaLike).filter(
      (f) => f.kind.kind === "string" || f.kind.kind === "enum" || f.kind.kind === "number" || f.kind.kind === "boolean",
    );
  }, [selectedNode]);

  const [name, setName] = useState("");
  const [hypothesis, setHypothesis] = useState("");
  const [prop, setProp] = useState<string>("");
  const [variantValue, setVariantValue] = useState<string>("");
  const [traffic, setTraffic] = useState(100);
  const [goal, setGoal] = useState("signup");
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Resolve the chosen field + the control's current value for that prop.
  const field = fields.find((f) => f.name === prop) ?? fields[0];
  const activeProp = field?.name ?? "";
  const controlValue =
    selectedNode && tree ? getNodeProp(tree, selectedNode.node_id, activeProp) : undefined;

  // No block selected → can't scope a test.
  if (!selectedNode) {
    return (
      <Card className="grid place-items-center px-5 py-8 text-center">
        <MousePointerClick className="h-5 w-5 text-subtle" strokeWidth={1.5} />
        <p className="mt-2 text-[13px] font-medium text-ink-strong">Select a block to test</p>
        <p className="mt-1 max-w-[32ch] text-[12px] leading-relaxed text-muted">
          Click a block in the preview, then come back here to vary one of its props against the
          control.
        </p>
      </Card>
    );
  }

  // Just created + launched → success state.
  if (createdId) {
    return (
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
            <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-ink-strong">Test is live</p>
            <p className="mt-1 text-[13px] leading-relaxed text-muted">
              “{name || "Your test"}” is now splitting traffic. Lift and a winner call show up in
              Signals as data comes in.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button variant="coral" size="sm" onClick={() => nav("/signals/experiments")}>
            View in Signals
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCreatedId(null);
              setName("");
              setHypothesis("");
              setVariantValue("");
              setErr(null);
            }}
          >
            New test
          </Button>
        </div>
      </Card>
    );
  }

  const busy = createExp.isPending || setStatus.isPending;
  const canSubmit = !!pageId && !!name.trim() && !!activeProp && !busy;

  const coerce = (raw: string): unknown => {
    if (!field) return raw;
    switch (field.kind.kind) {
      case "number": {
        const n = Number(raw);
        return Number.isFinite(n) ? n : raw;
      }
      case "boolean":
        return raw === "true";
      default:
        return raw;
    }
  };

  const submit = async () => {
    if (!pageId || !canSubmit) return;
    setErr(null);
    const body: CreateExperimentBody = {
      page_id: pageId,
      name: name.trim(),
      ...(hypothesis.trim() ? { hypothesis: hypothesis.trim() } : {}),
      scope: { node_id: selectedNode.node_id },
      variants: [
        { id: "control", is_control: true, weight: 50, label: "Control", ops: [] },
        {
          id: "variant",
          is_control: false,
          weight: 50,
          label: "Variant",
          ops: [{ node_id: selectedNode.node_id, prop: activeProp, value: coerce(variantValue) }],
        },
      ],
      goals: [{ event_name: goal.trim() || "signup", primary: true }],
      allocation: { traffic_pct: traffic },
    };
    try {
      const created = await createExp.mutateAsync(body);
      await setStatus.mutateAsync({ id: created.id, status: "running" });
      setCreatedId(created.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't launch the test.");
    }
  };

  return (
    <Card className="space-y-3.5 p-4">
      {/* what we're testing */}
      <div className="flex items-center gap-2 rounded-btn bg-paper-2 px-2.5 py-1.5 text-[12px] dark:bg-white/5">
        <FlaskConical className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.75} />
        <span className="text-muted">Testing</span>
        <span className="font-medium text-ink-strong">{selectedNode.component.name}</span>
        <span className="truncate font-mono text-[10px] text-subtle">{selectedNode.node_id}</span>
      </div>

      <Labeled label="Test name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Hero headline urgency"
          className={inputCls}
        />
      </Labeled>

      <Labeled label="Hypothesis" hint="optional">
        <textarea
          value={hypothesis}
          onChange={(e) => setHypothesis(e.target.value)}
          rows={2}
          placeholder="A time-bound promise lifts sign-ups vs. the generic headline."
          className={inputCls}
        />
      </Labeled>

      {fields.length === 0 ? (
        <p className="text-[12px] text-muted">
          This block has no varyable props. Pick a block with editable text or options.
        </p>
      ) : (
        <>
          <Labeled label="Prop to vary">
            <select
              value={activeProp}
              onChange={(e) => {
                setProp(e.target.value);
                setVariantValue("");
              }}
              className={inputCls}
            >
              {fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.name}
                </option>
              ))}
            </select>
          </Labeled>

          <div className="grid grid-cols-2 gap-2.5">
            <Labeled label="Control (current)">
              <div className="truncate rounded-btn border border-hairline bg-paper-2 px-2.5 py-1.5 text-[13px] text-muted dark:bg-white/5">
                {stringify(controlValue)}
              </div>
            </Labeled>
            <Labeled label="Variant">
              <VariantValueInput
                field={field}
                value={variantValue}
                onChange={setVariantValue}
                controlValue={typeof controlValue === "string" ? controlValue : ""}
                fieldLabel={`${selectedNode.component.name} ${activeProp}`}
              />
            </Labeled>
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2.5">
        <Labeled label="Traffic" hint={`${traffic}%`}>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={traffic}
            onChange={(e) => setTraffic(Number(e.target.value))}
            className="mt-2 w-full accent-accent"
          />
        </Labeled>
        <Labeled label="Goal event">
          <input
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="signup"
            className={cn(inputCls, "font-mono text-[12px]")}
          />
        </Labeled>
      </div>

      {err && <p className="text-[12px] text-accent">{err}</p>}

      <div className="flex items-center justify-end gap-2 border-t border-hairline pt-3">
        <Button variant="coral" size="sm" onClick={submit} disabled={!canSubmit}>
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
              Launching…
            </>
          ) : (
            <>
              <Rocket className="h-3.5 w-3.5" strokeWidth={1.75} />
              Launch test
            </>
          )}
        </Button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------- bits */
const inputCls =
  "w-full rounded-btn border border-hairline bg-paper px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5";

function Labeled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-[11px] font-medium text-ink">
        {label}
        {hint && <span className="font-mono text-[10px] text-subtle">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

function VariantValueInput({
  field,
  value,
  onChange,
  controlValue,
  fieldLabel,
}: {
  field: FieldDescriptor | undefined;
  value: string;
  onChange: (v: string) => void;
  /** Control's current copy — seeds the AI variant suggestions (#13). */
  controlValue?: string;
  fieldLabel?: string;
}) {
  if (field?.kind.kind === "enum") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">Choose…</option>
        {field.kind.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (field?.kind.kind === "boolean") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">Choose…</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (field?.kind.kind === "number") {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="New value"
        className={inputCls}
      />
    );
  }
  // String prop → free text, plus an AI "suggest a variant" picker seeded by
  // the control's current copy (#13).
  return (
    <div className="space-y-1">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="New value"
        className={inputCls}
      />
      {controlValue && controlValue.trim() && (
        <div className="flex justify-end">
          <AiRewrite current={controlValue} fieldLabel={fieldLabel ?? "copy"} onPick={onChange} />
        </div>
      )}
    </div>
  );
}

function stringify(v: unknown): string {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "string") return v.length > 40 ? v.slice(0, 40) + "…" : v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
